import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RadioField from '../fields/RadioField';
import './_styles_/Report.css';
import SelectField from '../fields/SelectField';
import { membersType } from '../../types';
import Constants from '../../utils/Constants';
import DownloadButton from '../buttons/DownloadButton';
import DateField, { YEAR } from '../fields/DateField';
import { toLocalDate } from '../../utils';

export const transformReportTypeToI18nKey = (reportType) => reportType.toLowerCase().replace(/_/g, '-');

Report.propTypes = {
    type: PropTypes.string.isRequired,
    versions: PropTypes.arrayOf(PropTypes.string).isRequired,
    formats: PropTypes.arrayOf(PropTypes.string).isRequired,
    deliveryYears: PropTypes.arrayOf(PropTypes.number).isRequired,
    members: membersType.isRequired,
    requiredFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    auctionId: PropTypes.number,
};

Report.defaultProps = {
    auctionId: null,
};

export const DELIVERY_YEAR_FIELD_NAME = 'deliveryYear';
export const AUCTION_YEAR_FIELD_NAME = 'auctionYear';
export const MEMBER_FIELD_NAME = 'member';
export const DATE_FROM_FIELD_NAME = 'dateFrom';
export const DATE_TO_FIELD_NAME = 'dateTo';

const mapVersionIds = (t) => (id) => ({ id, label: t(`reports.version.${id}`) });

const mapIdToAll = (id) => ({ id, label: id });
const sortDeliveryYears = (deliveryYears) => deliveryYears.sort((a, b) => b - a);
export const getDefaultFieldValue = (fieldName, fieldValues, requiredFields) => {
    let value = null;
    if (requiredFields.includes(fieldName)) {
        if (fieldValues.length > 1) {
            value = '';
        } else if (fieldValues.length === 1) {
            [value] = fieldValues;
        }
    }
    return value;
};

const extendWithZero = (partsToExtend, numOfZeros) => {
    for (let i = 0; i < numOfZeros; i++) {
        partsToExtend.push('0');
    }
};

const compareDifferentVersions = (a, b) => {
    let result = 0;
    const aParts = a.substring(1).split('_');
    const bParts = b.substring(1).split('_');
    const aPartsLength = aParts.length;
    const bPartsLength = bParts.length;

    let maxLength = aPartsLength;
    if (aPartsLength > bPartsLength) {
        maxLength = aPartsLength;
        extendWithZero(bParts, aPartsLength - bPartsLength);
        result = -1;
    } else if (aPartsLength < bPartsLength) {
        maxLength = bPartsLength;
        extendWithZero(aParts, bPartsLength - aPartsLength);
        result = 1;
    }

    for (let i = 0; i < maxLength; i++) {
        const aPart = parseInt(aParts[i], 10);
        const bPart = parseInt(bParts[i], 10);
        if (aPart > bPart) {
            result = -1;
        } else if (aPart < bPart) {
            result = 1;
        }
        if (result !== 0) {
            break;
        }
    }
    return result;
};

export const sortVersionsDesc = (versions) => versions.sort((a, b) => {
    let result = 0;
    if (a !== b) {
        result = compareDifferentVersions(a, b);
    }
    return result;
});

function Report(props) { // NOSONAR javascript:S3776
    const {
        type,
        versions,
        formats,
        deliveryYears,
        members,
        requiredFields,
        auctionId,
    } = props;
    const { t } = useTranslation();
    const sortedVersions = sortVersionsDesc([...versions]);
    const mappedVersions = sortedVersions.map(mapVersionIds(t));
    const [currentVersion, setCurrentVersion] = useState(sortedVersions[0]);
    const mappedFormats = formats.map(mapIdToAll);
    const [currentFormat, setCurrentFormatVersion] = useState(formats[0]);
    const [currentDeliveryYear, setCurrentDeliveryYear] = useState(getDefaultFieldValue(DELIVERY_YEAR_FIELD_NAME, sortDeliveryYears(deliveryYears), requiredFields));
    const [currentAuctionYear, setCurrentAuctionYear] = useState('');
    const defaultMember = getDefaultFieldValue(MEMBER_FIELD_NAME, members, requiredFields);
    const [currentMemberId, setCurrentMemberId] = useState(defaultMember ? defaultMember.memberId : defaultMember);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [isDetailVisible, setDetailVisible] = useState(false);
    const toggleDetailVisibility = () => setDetailVisible(!isDetailVisible);
    const isEnabled = (!requiredFields.includes(DELIVERY_YEAR_FIELD_NAME) || currentDeliveryYear !== '')
        && (!requiredFields.includes(MEMBER_FIELD_NAME) || currentMemberId !== '');
    const errorMessageKeys = {
        unauthorized: 'reports.download-failed-unauthorized',
        failed: 'reports.download-failed',
    };
    const data = {
        type,
        version: currentVersion,
        format: currentFormat,
        deliveryYear: requiredFields.includes(DELIVERY_YEAR_FIELD_NAME) ? parseInt(currentDeliveryYear, 10) : null,
        auctionYear: requiredFields.includes(AUCTION_YEAR_FIELD_NAME) ? new Date(currentAuctionYear).getFullYear() : null,
        member: requiredFields.includes(MEMBER_FIELD_NAME) ? currentMemberId : null,
        dateFrom: requiredFields.includes(DATE_FROM_FIELD_NAME) ? toLocalDate(dateFrom) : null,
        dateTo: requiredFields.includes(DATE_TO_FIELD_NAME) ? toLocalDate(dateTo) : null,
        auctionId,
    };
    return (
        <>
            <div className="report-item" onClick={toggleDetailVisibility} onKeyUp={toggleDetailVisibility} role="button" tabIndex={0}>
                {t(`reports.types.${transformReportTypeToI18nKey(type)}`)}
                <div className="report-item--arrow">
                    <FontAwesomeIcon icon={`caret-${isDetailVisible ? 'down' : 'right'}`} />
                </div>
            </div>
            {isDetailVisible && (
                <div className="report-item--attributes">
                    <RadioField
                        values={mappedVersions}
                        selectedValue={currentVersion}
                        onChange={setCurrentVersion}
                        inputIdBase="report-item--versions"
                        className="report-item--versions"
                    />
                    <RadioField
                        values={mappedFormats}
                        selectedValue={currentFormat}
                        onChange={setCurrentFormatVersion}
                        inputIdBase="report-item--formats"
                        className="report-item--formats"
                    />
                    {requiredFields.includes(DELIVERY_YEAR_FIELD_NAME) && deliveryYears.length > 1 && (
                        <SelectField
                            value={currentDeliveryYear}
                            onChange={setCurrentDeliveryYear}
                            emptyOption={<option value="" disabled>{t('reports.delivery-years')}</option>}
                            options={sortDeliveryYears(deliveryYears).map((deliveryYear) => <option value={deliveryYear} key={deliveryYear}>{deliveryYear}</option>)}
                            inputId="report-item--delivery-years"
                            className="report-item--delivery-years"
                        />
                    )}
                    {requiredFields.includes(MEMBER_FIELD_NAME) && members.length > 1 && (
                        <SelectField
                            value={currentMemberId}
                            onChange={setCurrentMemberId}
                            emptyOption={<option value="" disabled>{t('reports.members')}</option>}
                            options={members.map(({ memberId, balancingGroup }) => <option value={memberId} key={memberId}>{memberId} ({balancingGroup})</option>)}
                            inputId="report-item--members"
                            className="report-item--members"
                        />
                    )}
                    {requiredFields.includes(DATE_FROM_FIELD_NAME) && (
                        <DateField
                            value={dateFrom}
                            onChange={setDateFrom}
                            label={t('reports.from')}
                            inputId="report-item--date-from"
                            className="report-item--date-from"
                        />
                    )}
                    {requiredFields.includes(DATE_TO_FIELD_NAME) && (
                        <DateField
                            value={dateTo}
                            onChange={setDateTo}
                            label={t('reports.to')}
                            invalidBefore={dateFrom || null}
                            inputId="report-item--date-to"
                            className="report-item--date-to"
                        />
                    )}
                    {requiredFields.includes(AUCTION_YEAR_FIELD_NAME) && (
                        <DateField
                            value={currentAuctionYear}
                            onChange={setCurrentAuctionYear}
                            dateFormat={YEAR}
                            label={t('reports.auction-year')}
                            inputId="report-item--auction-year"
                            className="report-item--auction-year"
                        />
                    )}
                    <DownloadButton
                        urlPath={Constants.DOWNLOAD_REPORT_PATH}
                        data={data}
                        errorMessageKeys={errorMessageKeys}
                        disabled={!isEnabled}
                    />
                </div>
            )}
        </>
    );
}

export default Report;
